import type { KeyboardEvent, ReactNode } from 'react';
import styles from './CoverTile.module.css';

export type CoverTileProps = {
  title: string;
  coverUrl: string | null;
  badge?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  /** Shows the action but does not allow it (for example a broken game). */
  actionDisabled?: boolean;
  onClick?: () => void;
};

export function CoverTile({
  title,
  coverUrl,
  badge,
  actionLabel,
  onAction,
  actionDisabled = false,
  onClick,
}: CoverTileProps) {
  const interactive = onClick !== undefined;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      className={styles['tile']}
      data-interactive={interactive || undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? title : undefined}
      onClick={onClick}
      onKeyDown={interactive ? handleKeyDown : undefined}
    >
      {coverUrl !== null ? (
        <img
          className={styles['image']}
          src={coverUrl}
          alt={title}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      ) : (
        <div className={styles['fallback']}>
          <span className={styles['fallbackTitle']}>{title}</span>
        </div>
      )}
      {badge !== undefined && <div className={styles['badge']}>{badge}</div>}
      {actionLabel !== undefined && onAction !== undefined && (
        <div className={styles['actionBar']}>
          <button
            type="button"
            className={styles['action']}
            disabled={actionDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onAction();
            }}
          >
            {actionLabel}
          </button>
        </div>
      )}
    </div>
  );
}
